/**
 * Time Entry List Component
 * Displays list of time entries with edit/delete functionality
 */

import React, { useState } from 'react';
import { useTimeStore } from '../store/timeStore';
import { formatDate, formatDuration, formatHours, formatCurrency } from '../utils/formatters';
import TimeEntryModal from './TimeEntryModal';
import './TimeEntryList.css';

const TimeEntryList = ({ entries }) => {
  const deleteEntry = useTimeStore((state) => state.deleteEntry);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setShowModal(true);
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) {
      return;
    }

    try {
      await deleteEntry(entryId);
    } catch (error) {
      alert('Error deleting entry: ' + error.message);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingEntry(null);
  };

  // Filter out running timers from the list
  const completedEntries = entries.filter(e => !e.isRunning);

  if (completedEntries.length === 0) {
    return (
      <div className="empty-state">
        <p>No time entries yet. Start tracking your time!</p>
      </div>
    );
  }

  return (
    <>
      <div className="time-entry-list">
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Client</th>
              <th>Description</th>
              <th>Duration</th>
              <th>Rate</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {completedEntries.map(entry => (
              <tr key={entry.id}>
                <td>{formatDate(entry.startTime)}</td>
                <td>{entry.clientName}</td>
                <td>{entry.description || '-'}</td>
                <td>
                  {formatDuration(entry.duration)}
                  <br />
                  <span className="text-small">
                    ({formatHours(entry.duration)} hrs)
                  </span>
                </td>
                <td>{formatCurrency(entry.rate)}/hr</td>
                <td className="font-bold">{formatCurrency(entry.amount)}</td>
                <td>
                  {entry.invoiced ? (
                    <span className="badge badge-success">Invoiced</span>
                  ) : (
                    <span className="badge badge-info">Unbilled</span>
                  )}
                </td>
                <td>
                  <div className="action-buttons">
                    {!entry.invoiced && (
                      <>
                        <button
                          className="btn-icon"
                          onClick={() => handleEdit(entry)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handleDelete(entry.id)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <TimeEntryModal
          entry={editingEntry}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};

export default TimeEntryList;
